import { Anchor, Box, Button, FileButton, Image, LoadingOverlay, Text } from "@dehub/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { AuthApi, MediaApi, PartnerApi } from "@/apis";
import type { IUser } from "@/models/User";
import { authMe } from "@/store/Auth";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { usePermission } from "@/shared/permission";
import { message } from "@/utils/message";
import { renderBusinessName } from "@/utils/render-business-name";
import classes from "../PartnerPage.module.css";

type Props = Readonly<{
  user: IUser | null | undefined;
}>;

type BusinessRow = { profileName?: string | null; profileNameEng?: string | null; ref_code?: string | null };

type PartnerSummary = {
  logo?: string;
  businessName?: string;
  businessNameEng?: string | null;
  regNumber?: string;
  registerStatus?: string;
  type?: string;
  email?: string;
  phone?: string;
  businessAddress?: string;
};

function Empty() {
  return (
    <Text span className={classes.empty}>
      -
    </Text>
  );
}

export function PartnerProfileCard({ user }: Props) {
  const { t } = useTranslation("partner");
  const { t: tCommon } = useTranslation("common");
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  // PUT /prt/partner/logo is open to any role on the backend, so the screen is the only gate.
  const PRT_PRO_MAIN_EDIT = usePermission("PRT_PRO_MAIN_EDIT");

  const partner = user?.partner as PartnerSummary | undefined;
  const isVerified =
    partner?.registerStatus === "VERIFIED" || partner?.registerStatus === "ADMIN_VERIFIED";
  const displayName = renderBusinessName(partner);
  const initials = displayName === "-" ? "" : displayName.replace(/\s+/g, "").slice(0, 2).toUpperCase();

  const changeLogo = async (blob: File | null) => {
    if (!blob || !PRT_PRO_MAIN_EDIT.hasAccess) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", blob);
      const res = await MediaApi.uploadImage(body);
      await PartnerApi.uploadLogoProfile({ logo: res?.url ?? null });
      const auth = await AuthApi.me();
      dispatch(authMe(auth));
      message.success(t("uploadSuccess"));
    } catch (error: unknown) {
      const err = error as { message?: string };
      message.error(err?.message ?? tCommon("error"));
    } finally {
      setUploading(false);
    }
  };

  const contacts: { key: string; label: string; value?: string | null }[] = [
    { key: "email", label: t("card.email"), value: partner?.email },
    { key: "phone", label: t("card.phone"), value: partner?.phone },
    { key: "department", label: t("card.department"), value: user?.departmentUnitId },
    { key: "position", label: t("card.position"), value: user?.employeeUnitId },
    { key: "address", label: t("card.address"), value: partner?.businessAddress },
  ];

  const businesses: { key: string; kind: string; to: string; row: BusinessRow }[] = [
    ...(user?.buyers ?? []).map((row, i) => ({ key: `buyer-${i}`, kind: t("card.buyerKind"), to: "/business?type=buyer", row })),
    ...(user?.suppliers ?? []).map((row, i) => ({
      key: `supplier-${i}`,
      kind: t("card.supplierKind"),
      to: "/business?type=supplier",
      row,
    })),
  ];

  return (
    <Box component="aside" className={classes.profileCard}>
      <Box className={classes.profileHero}>
        <Box className={classes.logoBox}>
          {partner?.logo ? <Image src={partner.logo} alt={displayName} w="100%" h="100%" fit="cover" /> : initials}
          <LoadingOverlay visible={uploading} loaderProps={{ size: "sm" }} />
        </Box>
        <FileButton onChange={changeLogo} accept="image/png,image/jpeg">
          {(props) => (
            <ReasonTooltip reason={PRT_PRO_MAIN_EDIT.hasAccess ? null : <MissingPermission code="PRT_PRO_MAIN_EDIT" />}>
              <Button {...props} variant="light" color="gray" size="sm" radius="md" disabled={uploading}>
                {t("card.changeLogo")}
              </Button>
            </ReasonTooltip>
          )}
        </FileButton>
        <Text component="span" className={classes.businessName}>
          {displayName}
        </Text>
        <Box className={classes.pills}>
          <Box component="span" className={`${classes.pill} ${classes.pillMono}`}>
            {t("card.ttd")} {partner?.regNumber ?? "-"}
          </Box>
          <Box component="span" className={`${classes.pill} ${isVerified ? classes.pillVerified : classes.pillPending}`}>
            <Box component="span" className={classes.pillDot} />
            {isVerified ? t("card.verified") : t("card.pending")}
          </Box>
          {partner?.type ? (
            <Box component="span" className={classes.pill}>
              {partner.type === "COMPANY" ? t("card.company") : t("card.citizen")}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box className={classes.profileSection}>
        <Text component="span" className={classes.sectionCaps}>
          {t("contactInfo")}
        </Text>
        {contacts.map((item) => (
          <Box key={item.key} className={classes.contactRow}>
            <Text component="span" className={classes.contactLabel}>
              {item.label}
            </Text>
            <Text component="span" className={classes.contactValue}>
              {item.value ? item.value : <Empty />}
            </Text>
          </Box>
        ))}
      </Box>

      <Box className={classes.profileSection}>
        <Text component="span" className={classes.sectionCaps}>
          {t("card.business")}
        </Text>
        {businesses.length === 0 ? (
          <Text component="span" className={classes.bizEmpty}>
            {tCommon("notRegistered")}
          </Text>
        ) : (
          businesses.map((item) => (
            <Anchor key={item.key} component={Link} to={item.to} className={classes.bizRow}>
              <Box component="span" className={classes.bizText}>
                <Text component="span" className={classes.bizKind}>
                  {item.kind}
                </Text>
                <Text component="span" className={classes.bizName}>
                  {renderBusinessName(item.row)}
                </Text>
              </Box>
              {item.row.ref_code ? (
                <Text component="span" className={classes.bizCode} translate="no">
                  {item.row.ref_code}
                </Text>
              ) : null}
            </Anchor>
          ))
        )}
      </Box>
    </Box>
  );
}
